import { isServerValidationText, serverMessagesOf } from "src/utils/server-validation";
export type MapErrorCode =
  | "MAP_DATA_UNAVAILABLE"
  | "MAP_PREVIEW_BUSY"
  | "MAPS_DISABLED"
  | "RATE_LIMITED"
  | "NETWORK"
  | "UNKNOWN";

/** Backend codes this can tell apart from the body, both otherwise a 503. */
const BODY_CODES = new Set<string>(["MAP_DATA_UNAVAILABLE", "MAP_PREVIEW_BUSY", "MAPS_DISABLED"]);

/**
 * The backend answers 503 for two different reasons — its render queue full
 * (`MAP_PREVIEW_BUSY`, worth an automatic retry) vs. Overpass failing
 * (`MAP_DATA_UNAVAILABLE`, it won't) — so status alone can no longer tell
 * them apart; the body's `code` is read first, falling back to status.
 *
 * The preview is requested as a blob (`responseType: 'blob'`), so on a
 * non-2xx axios still runs the body through the blob parser: by the time an
 * error reaches here, `response.data` must already be the decoded
 * `{ code, message }` object, not the raw Blob — `fetchMapPreview` runs it
 * through `decodeBlobErrorBody` first.
 */
export function mapErrorCode(err: unknown): MapErrorCode {
  const response = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
  if (!response) return "NETWORK";
  const code = response.data?.code;
  if (typeof code === "string" && BODY_CODES.has(code)) return code as MapErrorCode;
  if (response.status === 503) return "MAP_DATA_UNAVAILABLE";
  if (response.status === 403) return "MAPS_DISABLED";
  if (response.status === 429) return "RATE_LIMITED";
  return "UNKNOWN";
}

/**
 * The backend's reason, as the canvas editor surfaces it. Null for
 * class-validator's English ("quantity must not be less than 1"): the caller
 * then shows its own translated copy (ops walk B8).
 */
export function apiMessage(err: unknown): string | null {
  const messages = serverMessagesOf(err);
  if (messages.length > 0) {
    if (messages.every(isServerValidationText)) return null;
    return messages.join(", ");
  }
  return err instanceof Error ? err.message : null;
}

/**
 * For a request sent with `responseType: "blob"`: axios hands the *error*
 * body over as a Blob too, so `response.data.code` is unreadable. This reads
 * the Blob's text as JSON and puts the object on `response.data` (as
 * `admin-gift-card.ts`'s `giftCardAdminBlobErrorMessage` does). A body that
 * is not JSON, or an error without a response, is returned as it came.
 */
export async function decodeBlobErrorBody(err: unknown): Promise<unknown> {
  const response = (err as { response?: { data?: unknown } } | null)?.response;
  if (!response || !(response.data instanceof Blob)) return err;
  try {
    response.data = JSON.parse(await response.data.text()) as unknown;
  } catch {
    // Not JSON (a proxy's HTML page, an empty body): mapErrorCode falls back to the status.
  }
  return err;
}
